/**
 * RBAC Permissions System
 *
 * Defines role-to-permission mappings and permission checking logic.
 * Privacy by design: journals and AI chats are NEVER accessible to others.
 */

import type {
  UserRole,
  Permission,
  PermissionCheckResult,
  DataVisibilityRules,
  FamilyRelationship,
  RoleAssignment,
} from './types';

/**
 * Permissions granted to each role
 *
 * Note: Some permissions (like content:view_others_journal) are NEVER
 * granted to any role - this is privacy by design.
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  student: [
    'self:read',
    'self:write',
    'self:delete',
    'content:view_own_progress',
    'content:view_own_journal',
    'content:view_own_ai_chats',
    'content:view_own_mood',
    'analytics:view_own',
  ],

  managed_child: [
    // Same as student - parent oversight is handled separately
    'self:read',
    'self:write',
    // Note: Cannot delete without parent approval
    'content:view_own_progress',
    'content:view_own_journal',
    'content:view_own_ai_chats',
    'content:view_own_mood',
    'analytics:view_own',
  ],

  parent: [
    'self:read',
    'self:write',
    'self:delete',
    'content:view_own_progress',
    'content:view_own_journal',
    'content:view_own_ai_chats',
    'content:view_own_mood',
    'analytics:view_own',
    // Family management
    'users:view_list',          // See family members
    'content:view_others_progress',  // See children's progress
    'billing:view',             // View family subscription
  ],

  counselor: [
    'self:read',
    'self:write',
    'content:view_own_progress',
    'content:view_own_journal',
    'content:view_own_ai_chats',
    'content:view_own_mood',
    'analytics:view_own',
    // Counselor-specific
    'users:view_list',
    'analytics:view_aggregate',
    'analytics:view_individual',     // Only for consented students
    'content:view_others_progress',
    'content:view_others_mood',      // Only for consented students
    'interventions:view',
    'interventions:create',
    'interventions:resolve',
  ],

  org_admin: [
    'self:read',
    'self:write',
    'self:delete',
    'content:view_own_progress',
    'content:view_own_journal',
    'content:view_own_ai_chats',
    'content:view_own_mood',
    'analytics:view_own',
    // Admin-specific
    'users:invite',
    'users:remove',
    'users:view_list',
    'users:edit_roles',
    'analytics:view_aggregate',
    'analytics:export',
    'content:view_others_progress',
    'interventions:view',
    'interventions:create',
    'interventions:assign',
    'interventions:resolve',
    'billing:view',
    'billing:manage',
    'org:view',
    'org:edit_settings',
    'org:edit_branding',
    'org:view_audit_log',
    'org:manage_cohorts',
  ],

  super_admin: [
    // All permissions except content:view_others_journal and content:view_others_ai_chats
    'self:read',
    'self:write',
    'self:delete',
    'content:view_own_progress',
    'content:view_own_journal',
    'content:view_own_ai_chats',
    'content:view_own_mood',
    'analytics:view_own',
    'users:invite',
    'users:remove',
    'users:view_list',
    'users:edit_roles',
    'analytics:view_aggregate',
    'analytics:view_individual',
    'analytics:export',
    'content:view_others_progress',
    'content:view_others_mood',
    'interventions:view',
    'interventions:create',
    'interventions:assign',
    'interventions:resolve',
    'billing:view',
    'billing:manage',
    'org:view',
    'org:edit_settings',
    'org:edit_branding',
    'org:view_audit_log',
    'org:manage_cohorts',
    'platform:manage_tenants',
    'platform:view_all_orgs',
    'platform:impersonate',
  ],
};

/**
 * Permissions that are NEVER granted to any role
 * These represent privacy-by-design constraints
 */
export const FORBIDDEN_PERMISSIONS: Permission[] = [
  'content:view_others_journal',
  'content:view_others_ai_chats',
];

/**
 * Check if a role has a specific permission
 */
export function roleHasPermission(role: UserRole, permission: Permission): boolean {
  // Privacy by design - these are never granted
  if (FORBIDDEN_PERMISSIONS.includes(permission)) {
    return false;
  }

  const rolePermissions = ROLE_PERMISSIONS[role];
  return rolePermissions?.includes(permission) ?? false;
}

/**
 * Check if a user has a permission based on their roles
 */
export function hasPermission(
  roles: RoleAssignment[],
  permission: Permission,
  context?: { type: 'organization' | 'cohort'; id: string }
): PermissionCheckResult {
  // Privacy by design - these are never granted
  if (FORBIDDEN_PERMISSIONS.includes(permission)) {
    return {
      allowed: false,
      reason: 'This content is private and cannot be accessed by others',
    };
  }

  // Check each role assignment
  for (const roleAssignment of roles) {
    // If context is required, check it matches
    if (context) {
      if (
        roleAssignment.contextType !== 'global' &&
        roleAssignment.contextType !== context.type
      ) {
        continue;
      }
      if (
        roleAssignment.contextType !== 'global' &&
        roleAssignment.contextId !== context.id
      ) {
        continue;
      }
    }

    // Check if this role has the permission
    if (roleHasPermission(roleAssignment.role, permission)) {
      return { allowed: true };
    }
  }

  return {
    allowed: false,
    reason: 'You do not have permission to perform this action',
  };
}

/**
 * Get the highest-privilege role from a list of role assignments
 */
export function getHighestRole(roles: RoleAssignment[]): UserRole {
  const rolePriority: UserRole[] = [
    'super_admin',
    'org_admin',
    'counselor',
    'parent',
    'student',
    'managed_child',
  ];

  for (const priorityRole of rolePriority) {
    if (roles.some(r => r.role === priorityRole)) {
      return priorityRole;
    }
  }

  return 'student'; // Default
}

/**
 * Check data visibility for a parent viewing a child's data
 */
export function getParentVisibilityRules(
  relationship: FamilyRelationship | null
): DataVisibilityRules {
  if (!relationship) {
    return {
      canViewProgress: false,
      canViewMood: false,
      canViewTechniques: false,
      canViewJournal: false,        // ALWAYS false
      canViewAiChats: false,        // ALWAYS false
      canViewCrisisFlags: false,
      isAggregate: false,
    };
  }

  // Even if relationship exists, journal and AI chats are NEVER visible
  return {
    canViewProgress: relationship.canViewProgress && relationship.childConsentGranted,
    canViewMood: relationship.canViewMood && relationship.childConsentGranted,
    canViewTechniques: relationship.canViewTechniques && relationship.childConsentGranted,
    canViewJournal: false,          // ALWAYS false - privacy by design
    canViewAiChats: false,          // ALWAYS false - privacy by design
    canViewCrisisFlags: relationship.childConsentGranted,
    isAggregate: false,
  };
}

/**
 * Check data visibility for a counselor viewing a student's data
 */
export function getCounselorVisibilityRules(
  hasConsent: boolean,
  isSameCohort: boolean
): DataVisibilityRules {
  if (!hasConsent || !isSameCohort) {
    return {
      canViewProgress: false,
      canViewMood: false,
      canViewTechniques: false,
      canViewJournal: false,
      canViewAiChats: false,
      canViewCrisisFlags: false,
      isAggregate: false,
    };
  }

  return {
    canViewProgress: true,
    canViewMood: true,
    canViewTechniques: true,
    canViewJournal: false,          // ALWAYS false - privacy by design
    canViewAiChats: false,          // ALWAYS false - privacy by design
    canViewCrisisFlags: true,
    isAggregate: false,
  };
}

/**
 * Check data visibility for an org admin
 */
export function getOrgAdminVisibilityRules(
  isOrgMember: boolean
): DataVisibilityRules {
  if (!isOrgMember) {
    return {
      canViewProgress: false,
      canViewMood: false,
      canViewTechniques: false,
      canViewJournal: false,
      canViewAiChats: false,
      canViewCrisisFlags: false,
      isAggregate: false,
    };
  }

  return {
    canViewProgress: true,
    canViewMood: false,             // Only aggregate
    canViewTechniques: true,
    canViewJournal: false,          // ALWAYS false - privacy by design
    canViewAiChats: false,          // ALWAYS false - privacy by design
    canViewCrisisFlags: true,
    isAggregate: true,              // Only aggregate data
  };
}

/**
 * Get all permissions for a role
 */
export function getPermissionsForRole(role: UserRole): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Check if a permission requires consent from the data subject
 */
export function requiresConsent(permission: Permission): boolean {
  const consentRequiredPermissions: Permission[] = [
    'analytics:view_individual',
    'content:view_others_mood',
    'interventions:view',
  ];

  return consentRequiredPermissions.includes(permission);
}

/**
 * Check if user can access feature based on subscription
 */
export function canAccessFeature(
  feature: keyof import('./types').PlanFeatures,
  planFeatures: Partial<import('./types').PlanFeatures>
): boolean {
  return planFeatures[feature] === true;
}

/**
 * Get the minimum role required for a permission
 */
export function getMinimumRoleForPermission(permission: Permission): UserRole | null {
  // Privacy by design - no role can have these
  if (FORBIDDEN_PERMISSIONS.includes(permission)) {
    return null;
  }

  const rolePriority: UserRole[] = [
    'managed_child',
    'student',
    'parent',
    'counselor',
    'org_admin',
    'super_admin',
  ];

  for (const role of rolePriority) {
    if (roleHasPermission(role, permission)) {
      return role;
    }
  }

  return null;
}
